import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Briefcase, Building2, TrendingUp, Hammer, Megaphone, Check, ArrowRight } from "lucide-react";

const ROLES = [
  { value: "entrepreneur", label: "Entrepreneur", icon: Briefcase, desc: "I'm building something new and looking for backing or partners." },
  { value: "business_owner", label: "Business Owner", icon: Building2, desc: "I run a business and want to grow with deals and contractors." },
  { value: "investor", label: "Investor", icon: TrendingUp, desc: "I deploy capital into opportunities and entrepreneurs." },
  { value: "contractor", label: "Contractor", icon: Hammer, desc: "I deliver services, gigs and project execution." },
  { value: "influencer", label: "Influencer", icon: Megaphone, desc: "I amplify brands and unlock audience reach." },
];

export default function RoleSelect() {
  const { setRole } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    try {
      const intended = sessionStorage.getItem("miresco_intended_role");
      if (intended && ROLES.some((r) => r.value === intended)) {
        setSelected(intended);
        sessionStorage.removeItem("miresco_intended_role");
      }
    } catch (e) { /* silent — sessionStorage may be unavailable in private mode */ }
  }, []);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    const res = await setRole(selected);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome, ${ROLES.find((r) => r.value === selected).label}.`);
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="role-select-page">
      <div className="max-w-5xl mx-auto px-6 py-12 lg:py-20">
        <div className="tag" data-testid="role-tag">
          <span className="w-1.5 h-1.5 bg-[#C8102E]" /> One last step
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-4">
          Who are you on MIRESCO?
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          Pick the role that fits you best. This shapes the opportunities you'll see and the matches we make. You can change it later.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-zinc-200">
          {ROLES.map((r, idx) => {
            const Icon = r.icon;
            const active = selected === r.value;
            return (
              <button
                key={r.value}
                onClick={() => setSelected(r.value)}
                data-testid={`role-card-${r.value}`}
                className={`text-left p-6 border-zinc-200 transition-colors relative
                  ${idx % 3 !== 2 ? "lg:border-r" : ""}
                  ${idx % 2 === 0 ? "sm:border-r lg:border-r" : ""}
                  ${idx < ROLES.length - 2 ? "border-b" : ""}
                  ${active ? "bg-[#C8102E] text-white" : "bg-white hover:bg-zinc-50"}
                `}
              >
                <div className="flex items-center justify-between">
                  <Icon className={`w-6 h-6 ${active ? "text-white" : "text-[#C8102E]"}`} />
                  {active && <Check className="w-5 h-5" />}
                </div>
                <div className="font-display font-black text-xl mt-3 tracking-tight">{r.label}</div>
                <p className={`text-sm mt-2 ${active ? "text-white/85" : "text-zinc-600"}`}>{r.desc}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
          <p className="text-xs text-zinc-500 font-mono">You can change your role from profile at any time.</p>
          <button
            onClick={submit}
            disabled={!selected || busy}
            data-testid="role-continue"
            className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 font-semibold transition-colors"
          >
            {busy ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
